import { useState } from "react";
import * as XLSX from "xlsx";
import { auth } from "../firebase";
import { API_BASE_URL } from "../config";
import Sidebar from "../components/Sidebar";
import { createPayrollRun } from "../services/financeCore/payrollService";

const money = (value) => `PKR ${Math.round(Number(value || 0)).toLocaleString("en-PK")}`;

// Mirrors functions/modules/payroll/helpers/parsePayroll.js's column
// detection exactly, so the persisted total matches what the backend
// actually built the bank files from — this parse is only for persistence,
// it never touches what gets sent to the server for file generation.
function normalizeKey(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function parseEmployeesClientSide(rows) {
  return rows.map((row) => {
    const map = {};
    Object.keys(row).forEach((k) => { map[normalizeKey(k)] = row[k]; });
    return {
      employeeId: map["employeeid"] || "",
      employeeName: map["employeename"] || "",
      netPay: Number(map["netpay"] || 0),
      bank: String(map["bank"] || "").trim()
    };
  }).filter((e) => e.employeeId || e.employeeName);
}

export default function GeneratePayroll() {
  const [file, setFile] = useState(null);
  const [accountingPeriod, setAccountingPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [lastRun, setLastRun] = useState(null);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setMessage("");
    setLastRun(null);
  };

  const upload = async () => {
    if (!file) {
      alert("Please select a payroll Excel file first.");
      return;
    }
    if (!accountingPeriod) {
      alert("Enter the accounting period (month) this payroll belongs to.");
      return;
    }

    const user = auth.currentUser;

    if (!user) {
      alert("Not logged in.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const token = await user.getIdToken();
      const form = new FormData();
      form.append("file", file);

      const res = await fetch(`${API_BASE_URL}/payroll/generate`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: form
      });

      if (!res.ok) {
        alert("Payroll generation failed.");
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = "PayrollFiles.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      // Persist the cost total, separate from bank-file generation — this is
      // the write GeneratePayroll never had before, feeding Operating
      // Expenses/Monthly P&L as "Permanent ALM Payroll".
      try {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: "array" });
        const rows = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { defval: "" });
        const employees = parseEmployeesClientSide(rows);
        const run = await createPayrollRun({ accountingPeriod, fileName: file.name, employees });
        setLastRun(run);
        setMessage(`Payroll files downloaded, and PKR ${money(run.totalNetPay)} across ${run.employeeCount} employee(s) recorded for ${accountingPeriod}.`);
      } catch (persistError) {
        setMessage(`Payroll files downloaded, but the cost total could not be recorded: ${persistError.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-shell">
      <Sidebar />

      <main className="main-content">
        <header className="page-header">
          <div>
            <div className="eyebrow">Payroll Operations</div>
            <h1 className="page-title">Generate Payroll</h1>
            <p className="page-copy">
              Upload a payroll workbook and download the generated payroll package. The total net pay is also
              recorded as ALM's permanent-staff payroll cost for Operating Expenses / Monthly P&amp;L.
            </p>
          </div>
        </header>

        <section className="card-grid">
          <div className="metric-card">
            <div className="metric-label">Selected File</div>
            <div className="metric-value" style={{ fontSize: 18 }}>
              {file ? file.name : "None"}
            </div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Output</div>
            <div className="metric-value" style={{ fontSize: 18 }}>ZIP Package</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Status</div>
            <div className="metric-value" style={{ fontSize: 18 }}>
              {loading ? "Processing" : "Ready"}
            </div>
          </div>
        </section>

        <section className="panel" style={{ maxWidth: 820 }}>
          <div className="panel-header">
            <div>
              <h2 className="panel-title">Payroll File Upload</h2>
              <p className="page-copy" style={{ marginTop: 4 }}>
                Use XLSX or XLSM payroll files for generation.
              </p>
            </div>
            <span className="badge">XLSX / XLSM</span>
          </div>

          <div className="panel-body">
            <label htmlFor="payroll-file" className="file-drop">
              <div className="file-title">
                {file ? file.name : "Choose payroll workbook"}
              </div>
              <div className="file-copy">
                {file ? `${Math.ceil(file.size / 1024)} KB selected` : "Select an Excel payroll file to begin"}
              </div>
            </label>

            <input
              id="payroll-file"
              type="file"
              accept=".xlsx,.xlsm"
              onChange={handleFileChange}
              className="hidden-input"
            />

            <div className="form-grid" style={{ marginTop: 18, maxWidth: 320 }}>
              <div className="field">
                <label>Accounting Period</label>
                <input className="input" type="month" value={accountingPeriod} onChange={(e) => setAccountingPeriod(e.target.value)} />
              </div>
            </div>

            <div className="button-row">
              <button
                type="button"
                onClick={upload}
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? "Generating..." : "Generate Payroll Files"}
              </button>
            </div>
            {message && <p className="page-copy" role="status" style={{ marginTop: 12 }}>{message}</p>}
          </div>
        </section>
      </main>
    </div>
  );
}
